import type { ITerrain } from "./types";
import {
  TERRAIN_COSINE_SEGMENTS,
  TERRAIN_COSINE_ROUGHNESS,
  TERRAIN_FRACTAL_REDUCTION,
  TERRAIN_FRACTAL_ROUGHNESS,
  TERRAIN_MULTISINE_STEPS,
  TERRAIN_MULTISINE_WAVELENGTH,
  TERRAIN_MULTISINE_OCTAVES,
  GAME_H,
} from "./data";
import { random, cos, PI, clamp } from "core/math";
import { perlin2D, perlinSeed } from "core/perlin";

export class CosineTerrain implements ITerrain {
  private _maxHeight: number = GAME_H * 0.15;
  private _segments: number = TERRAIN_COSINE_SEGMENTS;
  private _roughness: number = TERRAIN_COSINE_ROUGHNESS;
  private _lastY = 0;

  get maxSize(): number {
    return GAME_H * 0.2;
  }

  generate(width: number, _height: number, startAt?: number): number[] {
    const points: number[] = [];
    const partSize = width / this._segments;
    let prevY = startAt !== undefined ? startAt : this._lastY;
    const midY = this._maxHeight * 0.5;

    for (let s = 0; s < this._segments; s++) {
      const sx = s * partSize;
      const hillStartY = prevY;
      const hillSlices = Math.ceil(partSize / this._roughness);
      const randomHeight = this._maxHeight * 0.3 + random() * this._maxHeight * 0.5;
      const direction = random() < 0.5 ? 1 : -1;
      const hillDisplacement = s > 0 ? direction * randomHeight : 0;
      const targetY = clamp(hillStartY + hillDisplacement, -midY, midY);

      for (let i = 0; i <= hillSlices; i++) {
        const x = sx + i * this._roughness;
        if (x > width) break;
        const progress = i / hillSlices;
        const y =
          hillStartY +
          (targetY - hillStartY) * (1 - cos(PI * progress)) * 0.5 +
          randomHeight * cos(2 * PI * progress) * 0.3;
        points.push(y);
      }
      prevY = targetY;
    }

    this._lastY = prevY;
    return points;
  }
}

export class FractalTerrain implements ITerrain {
  private _maxHeight: number = GAME_H * 0.2;
  private _reduction: number = TERRAIN_FRACTAL_REDUCTION;
  private _roughness: number = TERRAIN_FRACTAL_ROUGHNESS;
  private _lastPoint = 0;

  get maxSize(): number {
    return GAME_H * 0.3;
  }

  generate(width: number, _height: number, startAt?: number): number[] {
    const halfW = width * 0.5;
    const first = this.generateHalf(0, halfW, 1, startAt);
    const second = this.generateHalf(halfW, width, -1, first[first.length - 1]);
    const points = [...first, ...second];
    this._lastPoint = points[points.length - 1];
    return points;
  }

  private generateHalf(sx: number, _ex: number, dir: number, startAt?: number): number[] {
    const start = startAt !== undefined ? startAt : this._lastPoint;
    const points: number[] = [start, start];
    let h = this._maxHeight * dir;

    for (let q = 0; q < this._roughness; q++) {
      h *= this._reduction;
      const newPoints: number[] = [points[0]];

      for (let i = 0; i < points.length - 1; i++) {
        const mid = (points[i] + points[i + 1]) * 0.5;
        let rand: number;
        if (q <= 1) {
          rand = Math.max(random(), random()) * h;
        } else {
          rand = random() * h;
        }
        newPoints.push(mid - rand);
        newPoints.push(points[i + 1]);
      }

      points.length = 0;
      points.push(...newPoints);
    }

    const result: number[] = Array.from({ length: points.length });
    for (let i = 0; i < points.length; i++) {
      result[i] = points[i];
    }
    return result;
  }
}

export class MultiSineTerrain implements ITerrain {
  private _maxHeight: number = GAME_H * 0.15;
  private _steps: number = TERRAIN_MULTISINE_STEPS;
  private _wavelength: number = TERRAIN_MULTISINE_WAVELENGTH;
  private _octaves: number = TERRAIN_MULTISINE_OCTAVES;
  private _seed: number[] = [];
  private _phaseOffset = 0;

  get maxSize(): number {
    return GAME_H * 0.3;
  }

  generate(_width: number, _height: number, startAt?: number): number[] {
    this._seed = [];
    for (let o = 0; o < this._octaves * 2; o++) {
      this._seed.push(random() * 100);
    }

    const raw = this.generatePoints(_width);
    if (startAt !== undefined && raw.length > 0) {
      const delta = startAt - raw[0];
      for (let i = 0; i < raw.length; i++) {
        raw[i] += delta;
      }
    }

    this._phaseOffset = raw.length > 0 ? raw[raw.length - 1] : 0;
    return raw;
  }

  private generatePoints(_width: number): number[] {
    const points: number[] = [];
    for (let i = 0; i < this._steps; i++) {
      const t = i / (this._steps - 1);
      let y = 0;
      for (let o = 0; o < this._octaves; o++) {
        const freq = (o + 1) * TERRAIN_MULTISINE_WAVELENGTH;
        const amp = this._maxHeight / (o + 1);
        y += amp * cos(2 * PI * (t * freq * 10 + this._seed[o]));
      }
      points.push(y);
    }
    return points;
  }
}

export class PerlinTerrain implements ITerrain {
  private _steps = 100;
  private _wavelength = 0.3;
  private _multi = 0.03;
  private _amplitude = 0.3;
  private _smoothPercentage = 0.8;
  private _smallOffsetX = 0;
  private _smallOffsetY = 0;
  private _bigOffsetX = 0;
  private _bigOffsetY = 0;

  get maxSize(): number {
    return GAME_H * 0.3;
  }

  generate(width: number, _height: number, startAt?: number): number[] {
    const steps = this._steps;
    const seed1 = (random() * 100) | 0;
    const seed2 = (random() * 100) | 0;

    this._smallOffsetX += steps / 100;
    this._smallOffsetY += steps / 100;

    const smallFreq = steps / 20;
    perlinSeed(seed1);
    const smallNoise: number[] = [];
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      smallNoise.push(perlin2D(t * smallFreq + this._smallOffsetX, this._smallOffsetY));
    }

    const bigWavelength = steps * this._wavelength;
    const bigSpeed = bigWavelength * 0.1;
    this._bigOffsetX -= bigSpeed;
    this._bigOffsetY += bigSpeed;

    perlinSeed(seed2);
    const bigNoise: number[] = [];
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      bigNoise.push(perlin2D(t * bigWavelength + this._bigOffsetX, this._bigOffsetY));
    }

    this.applySmooth(smallNoise, steps);
    this.applySmooth(bigNoise, steps);

    const points: number[] = [];
    for (let i = 0; i < steps; i++) {
      const soff = smallNoise[i] * width * this._multi;
      const boff = bigNoise[i] * width * this._amplitude;
      points.push(0 - soff - boff);
    }

    if (startAt !== undefined && points.length > 0) {
      const delta = startAt - points[0];
      for (let i = 0; i < points.length; i++) {
        points[i] += delta;
      }
    }

    return points;
  }

  private applySmooth(noise: number[], steps: number): void {
    const p = this._smoothPercentage;
    const ratioOffset = p * 0.5;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const smooth = t < ratioOffset ? t / ratioOffset : t > 1 - ratioOffset ? (1 - t) / ratioOffset : 1;
      noise[i] *= clamp(smooth, 0, 1);
    }
  }
}
